import classNames from "classnames";
import * as React from "react";

import {
  UserIcon,
  DeviceIcon,
  InvocationIcon,
  IntegrationIcon,
  OrgIcon,
  UnknownIcon,
} from "@/shared/components/icons";
import { Goto } from "@/shared/navigation";
import { Principal } from "@/shared/principal";

import { RobotoLinkHoverUnderline } from "../RobotoLinkHoverUnderline";

import styles from "./RobotoPrincipalUnderlineLink.module.css";

interface RobotoPrincipalUnderlineLinkProps {
  principal: Principal | string;
  icon?: boolean;
  className?: classNames.Argument;
}

type IconName =
  "user" | "org" | "device" | "invocation" | "integration" | "unknown";

const linkTextAndLinkToFromPrincipal = (
  principal: Principal | string,
): {
  linkText: string;
  linkTo: string;
  iconName: IconName;
} => {
  let principalAsObj: Principal;

  if (typeof principal === "string") {
    const principalFromStr = Principal.fromString(principal).ok;
    if (!principalFromStr) {
      return {
        linkTo: "#",
        linkText: principal,
        iconName: "unknown",
      };
    }
    principalAsObj = principalFromStr;
  } else {
    principalAsObj = principal;
  }

  switch (principalAsObj.ptype) {
    case "device":
      return {
        linkTo: Goto.createDeviceRoute(principalAsObj.unqualifiedId),
        linkText: principalAsObj.unqualifiedId,
        iconName: "device",
      };
    case "integration":
      return {
        linkTo:
          principalAsObj.id === "foxglove-import"
            ? Goto.route.Settings.Integrations.Foxglove
            : "#",
        linkText: principalAsObj.id,
        iconName: "integration",
      };
    case "invocation":
      return {
        linkTo: Goto.createInvocationRoute(principalAsObj.id),
        linkText: principalAsObj.id,
        iconName: "invocation",
      };
    case "user":
      return {
        linkTo: Goto.createUserRoute(principalAsObj.id),
        linkText: principalAsObj.id,
        iconName: "user",
      };
    case "org":
      // The org's page renders its content for System users only; anyone else
      // would land on its breadcrumbs above an empty body.
      return {
        linkTo: "#",
        linkText: principalAsObj.id,
        iconName: "org",
      };
    default:
      return {
        linkTo: "#",
        linkText: principalAsObj.id,
        iconName: "unknown",
      };
  }
};

const iconFromIconName = (iconName: IconName) => {
  switch (iconName) {
    case "user":
      return <UserIcon fontSize="small" color="primary" />;
    case "org":
      return <OrgIcon fontSize="small" color="primary" />;
    case "device":
      return <DeviceIcon fontSize="small" color="primary" />;
    case "invocation":
      return <InvocationIcon fontSize="small" color="primary" />;
    case "integration":
      return <IntegrationIcon fontSize="small" color="primary" />;
    case "unknown":
      return <UnknownIcon fontSize="small" color="primary" />;
  }
};

export const RobotoPrincipalUnderlineLink: React.FC<
  RobotoPrincipalUnderlineLinkProps
> = ({ principal, icon = false, className }) => {
  const { linkTo, linkText, iconName } =
    linkTextAndLinkToFromPrincipal(principal);

  return (
    <RobotoIconUnderlineLink
      // An unparseable / unknown principal (e.g. a bare id or email that isn't
      // a "ptype:id" string) has no real type, so suppress the misleading
      // "unknown" icon — but keep the label as a link.
      icon={icon && iconName !== "unknown" && iconFromIconName(iconName)}
      to={linkTo}
      className={className}
    >
      {linkText}
    </RobotoIconUnderlineLink>
  );
};

interface RobotoIconUnderlineLinkProps {
  icon?: React.ReactNode;
  to: string;
  children: React.ReactNode;
  className?: classNames.Argument;
}

/**
 * An icon beside a link that underlines on hover: how a principal renders
 * (Created by, Modified by), for a linked value that is not a principal.
 */
export function RobotoIconUnderlineLink({
  icon,
  to,
  children,
  className,
}: RobotoIconUnderlineLinkProps) {
  return (
    <div className={classNames(styles.container, className)}>
      {icon}
      <RobotoLinkHoverUnderline to={to}>{children}</RobotoLinkHoverUnderline>
    </div>
  );
}
